import { LECTERN_EYES, assessFraming, modelPupils, modelStatesAt } from './framing.js';
import { CODE_WORLD_CATALOGUE, stageOf, worldIn } from './world-catalogue.js';
import { distanceRule, footprintDistance, footprintReach, headingTo, modelStanding, modelFootprint, modelStopPoint, turnSamples, walkSamples, } from './model-distance.js';
import { faceSeconds, turnSeconds, walkSeconds } from './model-motion.js';
import { MODEL_CIRCLE, circleAngularSpeed, circleSamples } from './model-path.js';
import { FOG_VISIBLE, fogVisibility } from './sight.js';
import { MODEL_GAITS, MODEL_LIMITS, modelPosesAt, modelScale, modelSlugOf, modelSweptBox, replayModels, turnTowards, walkingGait, } from './models.js';
import { stepSeconds } from './motion-limits.js';
/**
 * Models over a lesson's steps with the pupil in the picture (ADR-043,
 * contract C3): where each is at a step, how long a step's models take, and
 * the distance rule judged at every step -- calm and Spannend, for every
 * pupil framing places, from a seated child to a tall adult. Built on the
 * calm replay (models.ts), framing's pupils (framing.ts) and the distance
 * maths (model-distance.ts).
 */
const xz = (v) => [v[0], v[2]];
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
/** The lesson's model objects the catalogue knows, with their facts and the size they are shown at. */
function knownModels(lesson, catalogue) {
    const known = new Map();
    for (const [id, object] of Object.entries(lesson.objects ?? {})) {
        const slug = modelSlugOf(object.kind);
        const facts = slug === undefined ? undefined : catalogue?.[slug];
        if (facts)
            known.set(id, { object, facts, scale: modelScale(object, facts) });
    }
    return known;
}
const hasModelObjects = (lesson) => Object.values(lesson.objects ?? {}).some((object) => modelSlugOf(object.kind) !== undefined);
/** Framing's pupils, computed only when there is a catalogue and a model to need them. */
function pupilsOf(lesson, catalogue, given, worlds) {
    if (given)
        return given;
    return catalogue && hasModelObjects(lesson)
        ? modelPupils(lesson, catalogue, 1.6, worlds)
        : undefined;
}
/**
 * The calm replay facing the pupils framing gives (the origin without a
 * catalogue), up to step `index`, with `pupil` -- when given -- as the eyes
 * at that step only.
 */
function calmReplay(lesson, catalogue, index, options = {}) {
    const pupils = pupilsOf(lesson, catalogue, options.pupils, options.worlds);
    const live = options.pupil;
    const framed = (i) => pupils ? (pupils[i] ? xz(pupils[i]) : undefined) : [0, 0];
    return replayModels(lesson, {
        catalogue,
        until: index,
        ...(options.spannend ? { spannendAt: index } : {}),
        ...(pupils || live ? { pupil: (i) => (live && i === index ? live : framed(i)) } : {}),
    });
}
/**
 * Each model object's pose once steps 0..`index` ran, the calm path: where it
 * stands, which way it faces and what it plays (contract C3). The same replay
 * `sceneStateAt` moves it by; with a catalogue its faces turn to the pupils
 * framing places (`modelPupils`), and its plays loop as the library says.
 */
export function modelStateAt(lesson, index, catalogue, worlds) {
    return new Map(modelPosesAt(calmReplay(lesson, catalogue, index, { worlds }), index));
}
const uniq = (clips) => [...new Set(clips)];
function pupilWalk(start, entry, move, facts, scale, pupil) {
    const gait = walkingGait(facts, entry['gait']);
    const clips = uniq([start.animation, gait, ...(move?.clips ?? [])]);
    const from = xz(start.pos);
    const to = modelStopPoint(from, pupil, facts, { scale, clips });
    const heading = headingTo(from, pupil, start.turn);
    const speed = facts.animations[gait]?.speed;
    const length = Math.hypot(to[0] - from[0], to[1] - from[1]);
    const seconds = turnSeconds(start.turn, heading) + (speed !== undefined ? walkSeconds(length, speed) : 0);
    return { from, to, turnFrom: start.turn, heading, gait, clips, seconds };
}
/** Step `index`'s Spannend entry for `object`, when it walks to the pupil. */
function pupilEntry(lesson, index, object) {
    const entries = lesson.steps[index]?.spannend?.models ?? [];
    const entry = entries.find((e) => e.object === object);
    return entry?.walkTo === 'pupil' ? { ...entry } : undefined;
}
/**
 * Where step `index`'s Spannend part walks `object` when the pupil's eyes are
 * at `pupil` ([x, z]): the walk, and where the engine stops it. The validator
 * and `modelSeconds` use this very function, so the headset clamps where they
 * judged. Undefined when that entry does not walk to the pupil, or the model
 * is not in the catalogue.
 */
export function spannendWalk(lesson, index, object, pupil, catalogue, options = {}) {
    const entry = pupilEntry(lesson, index, object);
    const model = knownModels(lesson, catalogue).get(object);
    if (!entry || !model)
        return undefined;
    const replay = calmReplay(lesson, catalogue, index, { ...options, pupil, spannend: true });
    const start = modelPosesAt(replay, index - 1).get(object);
    if (!start)
        return undefined;
    const move = replay.steps[index]?.get(object);
    return pupilWalk(start, entry, move, model.facts, model.scale, pupil);
}
/**
 * Seconds step `index`'s models take, walk + face + a once-only play, the
 * longest of them (contract C3; they all start with the step). Its
 * `spannend` part with `spannend: true`; a walk to the pupil walks to
 * `pupil` (the live eyes, for the engine), else to where framing places them.
 * `pupils` saves working out framing's pupils again.
 */
export function modelSeconds(lesson, index, catalogue, options = {}) {
    const step = lesson.steps[index];
    if (!isRecord(step))
        return 0;
    const spannend = options.spannend === true && isRecord(step['spannend']);
    const pupils = pupilsOf(lesson, catalogue, options.pupils, options.worlds);
    const replay = calmReplay(lesson, catalogue, index, {
        pupils,
        pupil: options.pupil,
        spannend,
        worlds: options.worlds,
    });
    let longest = 0;
    for (const move of replay.steps[index]?.values() ?? [])
        longest = Math.max(longest, move.seconds);
    if (!spannend || !catalogue)
        return longest;
    const pupil = options.pupil ?? (pupils?.[index] ? xz(pupils[index]) : [0, 0]);
    for (const move of replay.steps[index]?.values() ?? []) {
        const walk = spannendWalk(lesson, index, move.object, pupil, catalogue, {
            pupils,
            worlds: options.worlds,
        });
        if (!walk)
            continue;
        // Then it faces the eyes it walked at, and plays.
        const face = move.face ? faceSeconds(walk.heading, turnTowards(walk.to, pupil)) : 0;
        const rest = move.seconds - (move.face?.seconds ?? 0);
        longest = Math.max(longest, walk.seconds + face + rest);
    }
    return longest;
}
/**
 * How long a step's changes take with its models: they start together, so
 * the longer of `stepSeconds` (show, hide, move, highlight) and
 * `modelSeconds` -- what the headset waits for before "↻ Nog eens".
 */
export function stepSecondsWithModels(lesson, index, catalogue, options = {}) {
    const step = lesson.steps[index];
    if (!step)
        return 0;
    return Math.max(stepSeconds(step), modelSeconds(lesson, index, catalogue, options));
}
/**
 * Where an entry has the model during its step, and what it plays there: at the start what it was in
 * (and its gait, setting off), turning on the spot, walking in its gait or going round its ring in its
 * loop (ADR-054), then facing and at the end its play and what it ends in.
 */
function entryPlaces(start, move, reach, there) {
    const places = [];
    const walk = move?.walk;
    const circle = move?.circle;
    const gait = walk?.gait ?? circle?.gait;
    const setOff = uniq([start.animation, ...(gait ? [gait] : [])]);
    const ending = move ? move.clips.filter((clip) => clip !== gait) : [start.animation];
    if (there.start)
        places.push({ pos: xz(start.pos), turn: start.turn, clips: setOff });
    if (!there.end)
        return places;
    if (walk) {
        const { from, to, turnFrom, heading } = walk;
        for (const turn of turnSamples(turnFrom, heading, reach)) {
            places.push({ pos: from, turn, clips: setOff });
        }
        for (const pos of walkSamples(from, to))
            places.push({ pos, turn: heading, clips: [walk.gait] });
    }
    if (circle) {
        for (const turn of turnSamples(start.turn, circle.heading, reach)) {
            places.push({ pos: circle.start, turn, clips: setOff });
        }
        for (const sample of circleSamples(circle)) {
            places.push({ pos: sample.pos, turn: sample.turn, clips: [circle.gait] });
        }
    }
    // A turn in place (plan 4): its footprint swept through the turn, where it stands.
    if (move?.turned) {
        const pos = xz(move.end.pos);
        for (const turn of turnSamples(move.turned.from, move.turned.to, reach)) {
            places.push({ pos, turn, clips: [start.animation] });
        }
    }
    if (move?.face) {
        const pos = xz(move.end.pos);
        const clips = walk ? ending : uniq([start.animation, ...ending]);
        for (const turn of turnSamples(move.face.from, move.face.to, reach)) {
            places.push({ pos, turn, clips });
        }
    }
    const end = move?.end ?? start;
    // A loop ends as it goes round: its own clip, not none.
    const last = circle && ending.length === 0 ? [end.animation] : ending;
    places.push({ pos: xz(end.pos), turn: end.turn, clips: last });
    return places;
}
/** The places of a Spannend walk to the pupil: turning to face the eyes, walking, stopped. */
function walkPlaces(walk, move, reach) {
    const setOff = uniq([walk.clips[0], walk.gait]);
    const ending = move ? move.clips : [walk.gait];
    return [
        ...turnSamples(walk.turnFrom, walk.heading, reach).map((turn) => ({
            pos: walk.from,
            turn,
            clips: setOff,
        })),
        ...walkSamples(walk.from, walk.to).map((pos) => ({
            pos,
            turn: walk.heading,
            clips: [walk.gait],
        })),
        { pos: walk.to, turn: walk.heading, clips: ending },
    ];
}
/**
 * Every library model's places through every step of the calm path, as the distance rule walks them
 * (start, turn, walk, ring, face, end; a loop carried on from before): for "Niet in de wereld"
 * (scenery.ts). A Spannend walk to the pupil is not here: it comes through what the sight check
 * already found clear.
 */
export function modelPlaces(lesson, catalogue, worlds) {
    const out = [];
    if (lesson.mode !== 'vr')
        return out;
    const known = knownModels(lesson, catalogue);
    if (known.size === 0)
        return out;
    const pupils = modelPupils(lesson, catalogue, 1.6, worlds);
    const statesAt = modelStatesAt(lesson, catalogue, pupils, worlds);
    const pupilAt = (i) => (pupils[i] ? xz(pupils[i]) : undefined);
    const replay = replayModels(lesson, { catalogue, pupil: pupilAt });
    lesson.steps.forEach((step, index) => {
        const before = statesAt(index - 1);
        const after = statesAt(index);
        const start = modelPosesAt(replay, index - 1);
        for (const [id, { object, facts, scale }] of known) {
            const pose = start.get(id);
            const there = {
                start: before.get(id)?.visible === true,
                end: after.get(id)?.visible === true,
            };
            if (!pose || (!there.start && !there.end))
                continue;
            const move = replay.steps[index]?.get(id);
            const carried = move === undefined ? pose.circle : undefined;
            const clips = [pose.animation, ...(move?.clips ?? [])];
            const reach = footprintReach(modelFootprint(facts, [0, 0], 0, { scale, clips }), [0, 0]);
            const places = carried
                ? circleSamples(carried).map((s) => ({ pos: s.pos, turn: s.turn, clips: [pose.animation] }))
                : entryPlaces(pose, move, reach, there);
            out.push({ step: step.id, object: id, facts, scale, above: object.above ?? 0, places });
        }
    });
    return out;
}
const dutch = (n) => n.toFixed(2).replace('.', ',');
/**
 * The distance rule (spec "The distance rule", contract C3): at the start,
 * along every walk (every 0.25 m), through every turn and at the end of each
 * step, for the pupil framing places at every posture in `LECTERN_EYES`:
 *
 * - a small model (≤ 2.5 m high, ≤ 3 m long) keeps 1.5 m from the eyes;
 * - so does a static exhibit of any size (no animations: `isStaticModel`);
 * - a big one stays outside the stage's `modelClear` and `modelEyes` from the eyes (3 m and 3 m since
 *   ADR-084, `stageOf`);
 * - in a step's `spannend` part a big model may come in only by `walkTo:
 *   "pupil"`, which stops its front 2.5 m from the eyes (the engine's clamp,
 *   `spannendWalk`); anything else it does there keeps the calm rule.
 *
 * Footprints are the model's box swept by the clips it plays there, turned,
 * scaled and with 0.3 m round them.
 *
 * A model going round (ADR-054) is judged along its ring every 0.25 m, the whole ring for a loop and
 * in every later step it carries on through, with the same footprints and rule; its round keeps 45 s
 * (finite), 15°/s as each step's eyes see it, and the fog's 60 %; a model `above` the land keeps its
 * top within 35° of every posture's eyes.
 *
 * One problem per step, model, part and
 * kind of nearness, at its nearest over every pupil. Also every walk's 12 s,
 * calm and Spannend, timed as `modelSeconds` times it.
 */
export function assessModelDistance(lesson, catalogue, worlds) {
    const problems = [];
    if (lesson.mode !== 'vr')
        return problems;
    const known = knownModels(lesson, catalogue);
    if (known.size === 0)
        return problems;
    const pupils = modelPupils(lesson, catalogue, 1.6, worlds);
    const statesAt = modelStatesAt(lesson, catalogue, pupils, worlds);
    const pupilAt = (i) => (pupils[i] ? xz(pupils[i]) : undefined);
    const replay = replayModels(lesson, { catalogue, pupil: pupilAt });
    const framed = LECTERN_EYES.map((eye) => assessFraming(lesson, eye, { statesAt, ...(worlds ? { worlds } : {}) }, catalogue).steps.map((step) => step.eye));
    const eyes = framed.map((list) => list.map(xz));
    // The world's fog, for a ring (ADR-054): as spots, seen from the stage centre.
    const world = lesson.scene.type === 'world'
        ? worldIn(worlds ?? CODE_WORLD_CATALOGUE, lesson.scene.world)
        : undefined;
    const stage = stageOf(lesson, worlds);
    lesson.steps.forEach((step, index) => {
        const before = statesAt(index - 1);
        const after = statesAt(index);
        const start = modelPosesAt(replay, index - 1);
        const stepEyes = eyes.map((list) => list[index]).filter((e) => e !== undefined);
        const stepEyes3 = framed.map((list) => list[index]).filter((e) => e !== undefined);
        const calmIndex = new Map();
        (step.models ?? []).forEach((entry, e) => {
            if (!calmIndex.has(entry.object))
                calmIndex.set(entry.object, e);
        });
        /** The nearest the places come to their eyes, and the problems that makes. */
        const judge = (id, seen, rule, path, spannend) => {
            const { facts, scale } = known.get(id);
            let nearEyes = Infinity;
            let nearCentre = Infinity;
            for (const { places, eyes: from } of seen) {
                for (const place of places) {
                    const footprint = modelFootprint(facts, place.pos, place.turn, {
                        scale,
                        clips: place.clips,
                    });
                    for (const eye of from)
                        nearEyes = Math.min(nearEyes, footprintDistance(footprint, eye));
                    nearCentre = Math.min(nearCentre, footprintDistance(footprint, [0, 0]));
                }
            }
            const part = spannend ? ', spannend' : '';
            const base = { step: step.id, object: id, kind: 'distance', path };
            if (nearEyes < rule.eyes - 1e-6) {
                problems.push({
                    ...base,
                    en: `"${id}" comes within ${nearEyes.toFixed(2)} m of the pupil${spannend ? ' in the spannend part' : ''}; it keeps at least ${rule.eyes} m`,
                    nl: `stap "${step.id}"${part}: "${id}" komt te dicht bij de leerling (${dutch(nearEyes)} m; minstens ${String(rule.eyes).replace('.', ',')} m)`,
                });
            }
            if (rule.apron !== undefined && nearCentre < rule.apron - 1e-6) {
                problems.push({
                    ...base,
                    en: `"${id}" comes within ${nearCentre.toFixed(2)} m of the centre${spannend ? ' in the spannend part' : ''}; a big model stays outside the clear space round the stage (${rule.apron} m)`,
                    nl: `stap "${step.id}"${part}: "${id}" komt in de vrije ruimte rond het podium (${dutch(nearCentre)} m van het midden; minstens ${String(rule.apron).replace('.', ',')} m)`,
                });
            }
        };
        const tooLong = (id, seconds, path, spannend) => {
            if (seconds <= MODEL_LIMITS.walkSeconds + 1e-9)
                return;
            problems.push({
                path: `${path}/walkTo`,
                en: `"${id}" walks for ${seconds.toFixed(2)} s${spannend ? ' in the spannend part' : ''}; a walk takes at most ${MODEL_LIMITS.walkSeconds}`,
                nl: `stap "${step.id}"${spannend ? ', spannend' : ''}: "${id}" loopt ${dutch(seconds)} s (hooguit ${MODEL_LIMITS.walkSeconds})`,
                step: step.id,
                object: id,
                kind: 'walk-too-long',
            });
        };
        /** How far a footprint's corner swings while it turns, playing any of `clips`. */
        const reachOf = (id, clips) => {
            const { facts, scale } = known.get(id);
            return footprintReach(modelFootprint(facts, [0, 0], 0, { scale, clips }), [0, 0]);
        };
        const thereAt = (id) => ({
            start: before.get(id)?.visible === true,
            end: after.get(id)?.visible === true,
        });
        /** A circle's own checks (ADR-054): its 45 s, the pupil's 15°/s, the fog; once per step. */
        const circleChecks = (id, circle, move, path) => {
            const base = { step: step.id, object: id, path };
            if (move?.circle &&
                circle.rounds !== 'loop' &&
                move.seconds > MODEL_CIRCLE.secondsMax + 1e-9) {
                problems.push({
                    ...base,
                    kind: 'circle',
                    en: `"${id}" takes ${move.seconds.toFixed(2)} s for its round; a finite round takes at most ${MODEL_CIRCLE.secondsMax} s (or use rounds "loop")`,
                    nl: `stap "${step.id}": "${id}" doet ${dutch(move.seconds)} s over zijn rondje (hooguit ${MODEL_CIRCLE.secondsMax}; laat het doorgaan met rounds "loop")`,
                });
            }
            let fastest = 0;
            for (const eye of stepEyes)
                fastest = Math.max(fastest, circleAngularSpeed(circle, eye));
            if (fastest > MODEL_CIRCLE.angularMax + 1e-6) {
                problems.push({
                    ...base,
                    kind: 'comfort',
                    en: `"${id}" goes round at ${fastest.toFixed(1)}°/s as the pupil sees it; at most ${MODEL_CIRCLE.angularMax}°/s: a bigger round`,
                    nl: `stap "${step.id}": "${id}" gaat te snel rond voor de leerling (${Math.round(fastest)}°/s; hooguit ${MODEL_CIRCLE.angularMax}°/s): maak het rondje groter`,
                });
            }
            if (world?.fog !== undefined) {
                let least = 1;
                for (const sample of circleSamples(circle)) {
                    least = Math.min(least, fogVisibility(world.fog, Math.hypot(sample.pos[0], sample.pos[1])));
                }
                if (least < FOG_VISIBLE - 1e-9) {
                    const percent = Math.round(least * 100);
                    problems.push({
                        ...base,
                        kind: 'circle',
                        en: `"${id}"'s round goes into the fog (${percent} % visible; at least ${FOG_VISIBLE * 100} %)`,
                        nl: `stap "${step.id}": het rondje van "${id}" gaat de mist in (${percent} % zichtbaar; minstens ${FOG_VISIBLE * 100} %)`,
                    });
                }
            }
        };
        /** A raised model's top within 35° above every posture's eyes, wherever it is (ADR-054). */
        const heightCheck = (id, facts, scale, above, places) => {
            let steepest = -90;
            for (const place of places) {
                const footprint = modelFootprint(facts, place.pos, place.turn, {
                    scale,
                    clips: place.clips,
                });
                const top = above + modelSweptBox(facts, place.clips).max[1] * scale;
                for (const eye of stepEyes3) {
                    const d = footprintDistance(footprint, [eye[0], eye[2]]);
                    steepest = Math.max(steepest, (Math.atan2(top - eye[1], d) * 180) / Math.PI);
                }
            }
            if (steepest <= MODEL_CIRCLE.upMax + 1e-6)
                return;
            problems.push({
                step: step.id,
                object: id,
                path: `/objects/${id}/above`,
                kind: 'height',
                en: `"${id}" is ${Math.round(steepest)}° up from the pupil's eyes; at most ${MODEL_CIRCLE.upMax}°`,
                nl: `stap "${step.id}": "${id}" komt te hoog boven de leerling (${Math.round(steepest)}° omhoog; hooguit ${MODEL_CIRCLE.upMax}°)`,
            });
        };
        for (const [id, { object, facts, scale }] of known) {
            const pose = start.get(id);
            const there = thereAt(id);
            if (!pose || (!there.start && !there.end))
                continue;
            const move = replay.steps[index]?.get(id);
            const e = calmIndex.get(id);
            const path = e === undefined ? `/steps/${index}` : `/steps/${index}/models/${e}`;
            if (e !== undefined && move?.walk)
                tooLong(id, move.walk.seconds, path, false);
            // A loop from an earlier step that this one does not name goes on round its ring (ADR-054).
            const carried = move === undefined ? pose.circle : undefined;
            const places = carried
                ? circleSamples(carried).map((s) => ({ pos: s.pos, turn: s.turn, clips: [pose.animation] }))
                : entryPlaces(pose, move, reachOf(id, [pose.animation, ...(move?.clips ?? [])]), there);
            judge(id, [{ places, eyes: stepEyes }], distanceRule(modelStanding(facts, scale), false, stage), path, false);
            const circle = move?.circle ?? carried;
            if (circle)
                circleChecks(id, circle, move, e === undefined ? path : `${path}/circle`);
            // An entry that should go round but makes no ring: it stands on its centre, or its centre is
            // not there (assessModels says why for a wrong play or a walkTo beside it).
            const asked = e === undefined ? undefined : step.models?.[e];
            const goesRound = asked?.circle !== undefined &&
                asked.walkTo === undefined &&
                MODEL_GAITS.includes(asked.play ?? '');
            if (goesRound && !move?.circle) {
                problems.push({
                    step: step.id,
                    object: id,
                    path: `${path}/circle`,
                    kind: 'circle',
                    en: `"${id}" makes no round: it stands on its centre, or that centre is not there`,
                    nl: `stap "${step.id}": "${id}" heeft geen rondje: het staat op het midden, of dat midden is er niet`,
                });
            }
            const above = object.above ?? 0;
            if (above > 0)
                heightCheck(id, facts, scale, above, places);
        }
        const entries = step.spannend?.models ?? [];
        if (entries.length === 0)
            return;
        const spannend = replayModels(lesson, {
            catalogue,
            pupil: pupilAt,
            until: index,
            spannendAt: index,
        });
        const named = new Set();
        entries.forEach((entry, e) => {
            const id = entry.object;
            const model = known.get(id);
            const pose = start.get(id);
            const there = thereAt(id);
            if (!model || !pose || named.has(id) || (!there.start && !there.end))
                return;
            named.add(id);
            const standing = modelStanding(model.facts, model.scale);
            const move = spannend.steps[index]?.get(id);
            const path = `/steps/${index}/spannend/models/${e}`;
            if (entry.walkTo === 'pupil') {
                let longest = 0;
                const seen = [];
                for (const list of eyes) {
                    const pupil = list[index];
                    if (!pupil)
                        continue;
                    const walk = spannendWalk(lesson, index, id, pupil, catalogue, { pupils });
                    if (!walk)
                        continue;
                    longest = Math.max(longest, walk.seconds);
                    seen.push({ places: walkPlaces(walk, move, reachOf(id, walk.clips)), eyes: [pupil] });
                }
                tooLong(id, longest, path, true);
                judge(id, seen, distanceRule(standing, true, stage), path, true);
                return;
            }
            const places = entryPlaces(pose, move, reachOf(id, [pose.animation, ...(move?.clips ?? [])]), there);
            judge(id, [{ places, eyes: stepEyes }], distanceRule(standing, false, stage), path, true);
        });
    });
    return problems;
}
//# sourceMappingURL=model-steps.js.map